import type {
  ValidatableValue,
} from "libs/utils/validators/src/lib/validationRules";
import type {
  TCatFormDataInput,
  TUserFormData,
  TDeliveryData,
} from "@./suf-store";

import { flowPages, SignupFlowSlug } from "../constants";

import {
  getRequiredFieldsForSlug,
  validateSignUpField,
} from "./validateSignUpField";

export interface PrefilledFieldsStoreState {
  cats: Record<string, TCatFormDataInput>;
  user: TUserFormData;
  delivery: TDeliveryData;
}

function getByPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>(
    (acc, key) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[key] : undefined),
    obj,
  );
}

export function resolveFieldValue(
  fieldKey: string,
  state: PrefilledFieldsStoreState,
): ValidatableValue {
  const catMatch = /^cat\[([^\]]+)\]\.(.+)$/.exec(fieldKey);
  if (catMatch) {
    const [ , catId, path ] = catMatch;
    const cat = state.cats[catId];
    if (!cat) {
      return undefined;
    }
    return getByPath(cat as unknown as Record<string, unknown>, path) as ValidatableValue;
  }
  const merged = { ...state.user, ...state.delivery } as unknown as Record<string, unknown>;
  return getByPath(merged, fieldKey) as ValidatableValue;
}

/**
 * Validates all required fields for pages before `beforeSlug` using current store values.
 * This ensures `validatedFields` accurately reflects hydrated data before determining
 * the redirect target, so the CTA is visible on pages with pre-filled valid data.
 */
export function validatePrefilledFields(
  state: PrefilledFieldsStoreState,
  beforeSlug: SignupFlowSlug,
): void {
  const allSlugs = flowPages.map((p) => p.slug);
  const stopIdx = allSlugs.indexOf(beforeSlug);
  const slugsToValidate = stopIdx === -1 ? allSlugs : allSlugs.slice(0, stopIdx);

  for (const slug of slugsToValidate) {
    for (const fieldKey of getRequiredFieldsForSlug(slug)) {
      validateSignUpField(fieldKey, resolveFieldValue(fieldKey, state), slug);
    }
  }
}
